import React from "react";
import GoogleAddressTextField from '../GoogleAddressTextField';
import CountrySelectField from './CountrySelectField';
import { SelectedGoogleAddress } from '../../../utils/googleAddressAutocomplete';
import {
  Checkbox,
  Chip,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  TextField,
} from "@mui/material";
import type { MenuProps } from "@mui/material/Menu";
import CalendarDateInput from "./CalendarDateInput";
import { useNonModalSelect } from "./AnchoredMenu";
import { formatMemberPhoneNumberForDisplay, formatStaffPhoneInput } from "../../../utils/memberContactFormatting";
import { dashboardDensityClassNames } from "./DashboardDensityStyles";
import {
  adminChamberFieldSx,
  checkboxControlSx,
  choiceControlLabelSx,
  fieldSx,
  selectionChipSx,
} from "./formStyles";

export type FormTextFieldProps = {
  id: string;
  /**
   * Usually a plain string; accepts a node for mixed-weight labels such as
   * bold "Notes" with a regular-weight "(Optional)" suffix (step 3 mock).
   */
  label: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  type?: string;
  helperText?: string;
  description?: React.ReactNode;
  maxLength?: number;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  autoComplete?: string;
  disabled?: boolean;
  /** Locked but readable (grey #F2F2F3, not-allowed cursor), e.g. IF11 MB5 Member Name. */
  readOnly?: boolean;
  title?: string;
  onBlur?: () => void;
  containerClassName?: string;
  labelClassName?: string;
  descriptionClassName?: string;
  characterCountAlign?: "left" | "right";
  characterCountAccessory?: React.ReactNode;
  appearance?: "default" | "adminChamber";
  onAddressSelect?: (address: SelectedGoogleAddress) => void;
};

export const FormTextField = ({
  id,
  label,
  value,
  onChange,
  error,
  placeholder,
  required,
  multiline,
  rows,
  type,
  helperText,
  description,
  maxLength,
  inputMode,
  autoComplete,
  disabled,
  readOnly,
  title,
  onBlur,
  containerClassName = "mb-5 min-w-0",
  labelClassName = "mb-2 block text-sm font-bold leading-5 text-gray-950",
  descriptionClassName = "mb-3 text-sm leading-6 text-black",
  characterCountAlign = "right",
  characterCountAccessory,
  appearance = "default",
  onAddressSelect,
}: FormTextFieldProps) => (
  <div
    className={`${containerClassName} ${dashboardDensityClassNames.formField}`}
  >
    <label htmlFor={id} className={labelClassName}>
      {label}
      {required && <span className="text-red-500">*</span>}
    </label>
    {description && <div className={descriptionClassName}>{description}</div>}
    {onAddressSelect ? <GoogleAddressTextField
      id={id} value={value} onValueChange={onChange} onAddressSelect={onAddressSelect}
      autoComplete={autoComplete} onBlur={onBlur} placeholder={placeholder} fullWidth
      required={required} error={!!error} helperText={error || helperText} disabled={disabled}
      inputProps={maxLength ? { maxLength } : undefined}
      sx={appearance === "adminChamber" ? adminChamberFieldSx : fieldSx}
    /> : <TextField
      id={id}
      autoComplete={autoComplete}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      fullWidth
      required={required}
      type={type}
      multiline={multiline}
      rows={rows}
      error={!!error}
      helperText={error || helperText}
      disabled={disabled}
      inputProps={
        maxLength || inputMode || readOnly || title
          ? {
              ...(maxLength ? { maxLength } : {}),
              ...(inputMode ? { inputMode } : {}),
              ...(readOnly ? { readOnly: true } : {}),
              ...(title ? { title } : {}),
            }
          : undefined
      }
      sx={readOnly
        ? [appearance === "adminChamber" ? adminChamberFieldSx : fieldSx,
          { "& .MuiInputBase-root": { backgroundColor: "#F2F2F3" }, "& .MuiInputBase-input": { cursor: "not-allowed" } }] as any
        : appearance === "adminChamber" ? adminChamberFieldSx : fieldSx}
    />}
    {maxLength && (
      <div
        className={`mt-1 flex flex-wrap items-center gap-3 text-xs text-gray-500 ${
          characterCountAlign === "left" ? "justify-between" : "justify-end"
        }`}
      >
        <span>
          {value.length}/{maxLength} characters
        </span>
        {characterCountAccessory}
      </div>
    )}
  </div>
);

export type FormPhoneFieldProps = Omit<
  FormTextFieldProps,
  "inputMode" | "type"
>;

/**
 * A saved value shows as stored: only unambiguous NANP values are formatted,
 * and international, vanity, extension-bearing, short, and multi-number
 * values stay as they are. A user edit keeps 10 digits and formats as
 * (XXX) XXX-XXXX (formatStaffPhoneInput, Monday 13399804125).
 */
export const FormPhoneField = ({
  onChange,
  value,
  ...props
}: FormPhoneFieldProps) => (
  <FormTextField
    {...props}
    type="tel"
    inputMode="tel"
    value={formatMemberPhoneNumberForDisplay(value)}
    onChange={(nextValue) => onChange(formatStaffPhoneInput(nextValue))}
  />
);

export type FormCountryFieldProps = Pick<
  FormTextFieldProps,
  | "id"
  | "label"
  | "value"
  | "onChange"
  | "error"
  | "required"
  | "disabled"
  | "helperText"
  | "containerClassName"
  | "labelClassName"
  | "appearance"
>;

/**
 * Country field with the FormTextField label and skin, backed by the shared
 * design Country dropdown (CMP Dashboard (20) CLAUDE.md "Country dropdown").
 */
export const FormCountryField = ({
  id,
  label,
  value,
  onChange,
  error,
  required,
  disabled,
  helperText,
  containerClassName = "mb-5 min-w-0",
  labelClassName = "mb-2 block text-sm font-bold leading-5 text-gray-950",
  appearance = "default",
}: FormCountryFieldProps) => (
  <div
    className={`${containerClassName} ${dashboardDensityClassNames.formField}`}
  >
    <label htmlFor={id} className={labelClassName}>
      {label}
      {required && <span className="text-red-500">*</span>}
    </label>
    <CountrySelectField
      id={id}
      value={value}
      onValueChange={onChange}
      fullWidth
      required={required}
      error={!!error}
      helperText={error || helperText}
      disabled={disabled}
      sx={appearance === "adminChamber" ? adminChamberFieldSx : fieldSx}
    />
  </div>
);

export type FormCalendarDateFieldProps = {
  id: string;
  label: string;
  /** yyyy-mm-dd, same storage format as the previous native date inputs. */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  /** Guidance rendered directly under the label, above the input. */
  description?: React.ReactNode;
  /**
   * Calendar icon side. The step 3 mocks show the icon on the LEFT for the
   * Member Insights year field but on the RIGHT for the Member Referral
   * Tracking and Affinity Program date fields.
   */
  iconPosition?: "left" | "right";
};

/**
 * Date field for member workflows backed by the shared CalendarDateInput so
 * empty values show a real placeholder and the picker opens on field click
 * (MEM-CREATE-008/009/010).
 */
export const FormCalendarDateField = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  error,
  required,
  disabled,
  description,
  iconPosition = "left",
}: FormCalendarDateFieldProps) => (
  <div
    className={`mb-5 min-w-0 ${dashboardDensityClassNames.formField}`}
  >
    <label
      htmlFor={id}
      className="mb-2 block text-sm font-bold leading-5 text-gray-950"
    >
      {label}
      {required && <span className="text-red-500">*</span>}
    </label>
    {description && (
      <div className="mb-3 text-sm leading-6 text-black">{description}</div>
    )}
    <CalendarDateInput
      id={id}
      ariaLabel={label}
      mode="fullDate"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      iconPosition={iconPosition}
      error={Boolean(error)}
    />
    {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
  </div>
);

export type FormSelectFieldProps = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: React.ReactNode; disabled?: boolean }[];
  placeholder: string;
  error?: string;
  helperText?: string;
  /** Guidance rendered directly under the label, above the input (matches FormTextField). */
  description?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  onBlur?: () => void;
  containerClassName?: string;
  labelClassName?: string;
  /**
   * Custom rendering for the selected value (e.g. status color dots). Receives
   * the raw selected value; callers must handle the empty-value placeholder.
   */
  selectRenderValue?: (value: string) => React.ReactNode;
  menuContainer?: MenuProps["container"];
  appearance?: "default" | "adminChamber";
};

export const FormSelectField = ({
  id,
  label,
  value,
  onChange,
  options,
  placeholder,
  error,
  helperText,
  description,
  required,
  disabled,
  onBlur,
  containerClassName = "mb-5 min-w-0",
  labelClassName = "mb-2 block text-sm font-bold leading-5 text-gray-950",
  selectRenderValue,
  menuContainer,
  appearance = "default",
}: FormSelectFieldProps) => {
  const nonModalSelect = useNonModalSelect(
    menuContainer ? { container: menuContainer } : undefined,
  );
  return (
  <div
    className={`${containerClassName} ${dashboardDensityClassNames.formField}`}
  >
    <label htmlFor={id} className={labelClassName}>
      {label}
      {required && <span className="text-red-500">*</span>}
    </label>
    {description && (
      <div className="mb-3 text-sm leading-6 text-black">{description}</div>
    )}
    <TextField
      id={id}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onBlur={onBlur}
      select
      fullWidth
      error={!!error}
      helperText={error || helperText}
      disabled={disabled}
      sx={appearance === "adminChamber" ? adminChamberFieldSx : fieldSx}
      SelectProps={{
        ...nonModalSelect.selectProps,
        displayEmpty: true,
        renderValue: selectRenderValue
          ? (selectedValue) => selectRenderValue(selectedValue as string)
          : undefined,
      }}
    >
      <MenuItem value="" disabled>
        {placeholder}
      </MenuItem>
      {options.map((option) => (
        <MenuItem
          key={option.value}
          value={option.value}
          disabled={option.disabled}
        >
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  </div>
  );
};

export type FormMultiSelectFieldProps = {
  id: string;
  label: string;
  selected: string[];
  onChange: (selected: string[]) => void;
  options: string[];
  placeholder: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  getOptionLabel?: (value: string) => string;
  showSelectionsBelow?: boolean;
  emptyOptionsText?: string;
  emptySelectionText?: string;
  menuMaxHeight?: number;
  wrapLabels?: boolean;
};

const wrappedSelectionChipSx = {
  ...selectionChipSx,
  height: "auto",
  minHeight: 24,
  minWidth: 0,
  maxWidth: "100%",
  "& .MuiChip-label": {
    minWidth: 0,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    textOverflow: "clip",
    paddingTop: "2px",
    paddingBottom: "2px",
  },
  "& .MuiChip-deleteIcon": {
    ...selectionChipSx["& .MuiChip-deleteIcon"],
    flexShrink: 0,
  },
};

export const FormMultiSelectField = ({
  id,
  label,
  selected,
  onChange,
  options,
  placeholder,
  error,
  required,
  disabled,
  getOptionLabel = (value) => value,
  showSelectionsBelow = false,
  emptyOptionsText,
  emptySelectionText,
  menuMaxHeight,
  wrapLabels = false,
}: FormMultiSelectFieldProps) => {
  const nonModalSelect = useNonModalSelect(
    menuMaxHeight
      ? { PaperProps: { style: { maxHeight: menuMaxHeight } } }
      : undefined,
  );
  return (
  <div
    className={`mb-5 min-w-0 ${dashboardDensityClassNames.formField}`}
  >
    <label
      htmlFor={id}
      className="mb-2 block text-sm font-bold leading-5 text-gray-950"
    >
      {label}
      {required && <span className="text-red-500">*</span>}
    </label>
    {options.length === 0 && emptyOptionsText && (
      <p className="mb-2 text-sm text-[#727272]">{emptyOptionsText}</p>
    )}
    <TextField
      id={id}
      value={selected}
      onChange={(event) => {
        const value = event.target.value;
        onChange(typeof value === "string" ? value.split(",") : value);
      }}
      select
      fullWidth
      error={!!error}
      helperText={error}
      disabled={disabled}
      sx={fieldSx}
      SelectProps={{
        ...nonModalSelect.selectProps,
        multiple: true,
        displayEmpty: true,
        renderValue: (value) => {
          const selectedValues = value as string[];

          if (selectedValues.length === 0) {
            return placeholder;
          }

          if (showSelectionsBelow) {
            return `${selectedValues.length} selected`;
          }

          return (
            <span className="flex flex-wrap gap-1">
              {selectedValues.map((selectedValue) => (
                <Chip
                  key={selectedValue}
                  label={getOptionLabel(selectedValue)}
                  size="small"
                  sx={wrapLabels ? wrappedSelectionChipSx : selectionChipSx}
                  // Stop the Select from opening/toggling when removing a chip.
                  onMouseDown={(event) => event.stopPropagation()}
                  onDelete={
                    disabled
                      ? undefined
                      : () =>
                          onChange(
                            selected.filter((item) => item !== selectedValue)
                          )
                  }
                />
              ))}
            </span>
          );
        },
      }}
    >
      {options.map((option) => (
        <MenuItem
          key={option}
          value={option}
          sx={wrapLabels ? { whiteSpace: "normal" } : undefined}
        >
          <Checkbox
            checked={selected.includes(option)}
            sx={wrapLabels ? { flexShrink: 0 } : undefined}
          />
          <span
            className="text-sm text-gray-950"
            style={wrapLabels ? {
              flex: "1 1 auto",
              minWidth: 0,
              whiteSpace: "normal",
              overflowWrap: "anywhere",
            } : undefined}
          >
            {getOptionLabel(option)}
          </span>
        </MenuItem>
      ))}
    </TextField>
    {showSelectionsBelow && (
      selected.length ? (
        <div role="list" aria-label={`${label} selections`} className="mt-2 flex flex-wrap gap-2">
          {selected.map((value) => (
            <Chip
              key={value}
              role="listitem"
              tabIndex={-1}
              label={getOptionLabel(value)}
              size="small"
              sx={wrapLabels ? wrappedSelectionChipSx : selectionChipSx}
              disabled={disabled}
              onDelete={disabled ? undefined : () => onChange(selected.filter((item) => item !== value))}
              deleteIcon={
                <button
                  type="button"
                  aria-label={`Remove group ${getOptionLabel(value)}`}
                  title="Remove group"
                  disabled={disabled}
                >
                  <span aria-hidden="true">×</span>
                </button>
              }
            />
          ))}
        </div>
      ) : emptySelectionText ? (
        <p className="mt-2 text-sm text-[#727272]">{emptySelectionText}</p>
      ) : null
    )}
  </div>
  );
};

export type MultiOptionGroupProps = {
  label: string;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  error?: string;
  required?: boolean;
  columns?: "compact" | "inline" | "single";
  disabled?: boolean;
};

// Phase A note: checking any option strips "None" from the selection — a
// member-workflow quirk carried verbatim from the donor. Generalize to an
// `exclusiveOption?: string` prop before non-member consumers adopt this.
export const MultiOptionGroup = ({
  label,
  options,
  selected,
  onChange,
  error,
  required,
  columns = "compact",
  disabled,
}: MultiOptionGroupProps) => (
  <div className="mb-5">
    <p className="mb-2 text-sm font-bold text-gray-950">
      {label}
      {required && <span className="text-red-500">*</span>}
    </p>
    <div
      className={
        columns === "inline"
          ? "grid grid-cols-1 gap-x-5 gap-y-2 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-7"
          : columns === "single"
            ? "grid grid-cols-1 gap-y-2"
            : "grid grid-cols-1 gap-x-5 gap-y-2 sm:grid-cols-2"
      }
    >
      {options.map((option) => (
        <FormControlLabel
          key={option}
          sx={choiceControlLabelSx}
          control={
            <Checkbox
              checked={selected.includes(option)}
              disabled={disabled}
              onChange={(event) => {
                if (event.target.checked) {
                  onChange([
                    ...selected.filter((item) => item !== "None"),
                    option,
                  ]);
                } else {
                  onChange(selected.filter((item) => item !== option));
                }
              }}
              sx={checkboxControlSx}
            />
          }
          label={<span className="block text-sm text-gray-900">{option}</span>}
        />
      ))}
    </div>
    {error && <FormHelperText error>{error}</FormHelperText>}
  </div>
);
